import * as THREE from 'three';
import type { CritterSettings, MapDefinition, Point } from '../../../shared/game/world-types.js';
import type { FogState } from '../../../shared/game/fog-types.js';
import { CritterGround, type Habitat } from './critter-ground.js';
import { between, smooth, type CritterWorld, type Random } from './critter-math.js';
import { releaseCritter } from './critter-parts.js';
import { BirdBrain, type BirdMode } from './bird-brain.js';
import { BirdModel, restingAction } from './bird-model.js';
import { BIRDS, CROW, GULL, type BirdKind, type BirdSpecies } from './bird-species.js';
import { FlyoverBird, planFlyover, unprojectAt } from './flyover.js';
import { HauntFlock, hauntDistance } from './haunt.js';
import { ButterflyModel, Flutterer } from './butterfly.js';
import { CRAB_SCALE, CrabBrain, CrabModel, restingCrab } from './crab.js';
import { GrazerBrain } from './grazer-brain.js';
import { QuadModel, restingQuad } from './quad-model.js';
import { QUADS, type QuadKind } from './quad-species.js';
import { RABBIT_SCALE, RabbitBrain } from './rabbit-brain.js';
import { RabbitModel } from './rabbit-model.js';
import type { CritterCue } from '../../audio/critter-sounds.js';

/** What the scene knows each frame. */
export interface CritterContext {
  /** Where the party stands: critters appear near it and shy away from it. Without a party (the lobby) the camera's focus stands in. */
  party: Point[];
  /** The party's vision. Absent before a world exists: everything is on show and critters stay off-screen when they appear. */
  fog?: FogState;
  /** Further things to keep clear of besides the party, such as the splash screen's bandits. */
  threats?: Point[];
  /** 0 by day, 1 at night: the wildlife keeps its hours by it. */
  night?: number;
  /** False while the scene shows no world at all. */
  active: boolean;
}

/** A fixed cast for a scene with its own staging, such as the splash screen: where crows and rabbits stand. */
export interface CritterCast { crows: Point[]; rabbits: Point[] }

/** A small seeded generator (mulberry32). */
function speechDice(seed: number): Random {
  let state = seed >>> 0;
  return () => { state = (state + 0x6d2b79f5) >>> 0; let t = state; t = Math.imul(t ^ t >>> 15, t | 1); t ^= t + Math.imul(t ^ t >>> 7, t | 61); return ((t ^ t >>> 14) >>> 0) / 4294967296; };
}

/** Ground critters further than this from the party are quietly retired; birds already aloft are given less room. */
const CULL_DISTANCE = 75, FLIGHT_CULL_DISTANCE = 60;
/** A critter is shown out to this far past the edge of the party's vision, where the fog veil has closed, so none shrinks or pops while it can still be seen. */
const SIGHT_PAD = 2.5;
const MAX_FLYERS = 8;
const MAX_FLIGHT_SECONDS = 24;
/** A haunt's flock is in the air while the party is within this many metres of its airspace, and put away beyond the second. */
const HAUNT_RANGE = 110, HAUNT_LEAVE = 150;
/** Within this many metres of the shore a flyover is usually gulls. */
const COAST_REACH = 60;

interface Ground<Brain extends { x: number; z: number }, Model> { brain: Brain; model: Model; root: THREE.Object3D; scale: number; appear: number; age: number; lifetime: number }
interface Flyer { bird: FlyoverBird; model: BirdModel; species: BirdSpecies }
type BeastKind = QuadKind | 'crab';
/** A grazer or a crab: whatever the kind, the field needs to step it, place it and ask what it is afraid of. */
interface Beast {
  kind: BeastKind; brain: { x: number; z: number }; root: THREE.Object3D; scale: number; appear: number; age: number; lifetime: number;
  step(dt: number, world: CritterWorld): void; heading(): number; gone(): boolean; fear(): Point | null; mode(): string;
}
/** When a bird, flock bird or sheep next speaks, and what it, a rabbit or a deer was doing the last time it was looked at. */
interface Voice { mode: BirdMode | 'bolt' | 'flee' | 'watch' | null; next: number; crop: number }
/** A handful of butterflies working one patch of flowers. */
interface Patch { centre: Point; flutterers: Flutterer[]; scales: number[]; appear: number; age: number; lifetime: number }
export type CritterKind = 'rabbit' | BirdKind | BeastKind | 'butterfly';

/**
 * Ambient wildlife, drawn for atmosphere alone: rabbits browsing the open ground, crows working it and gulls loafing on the
 * strand, flocks always on the wing over the places the map names, and now and then a flock crossing the screen high above the party. Critters cannot be clicked, targeted or collided with, never reach the
 * server, and are rolled fresh in each browser. They follow game time, so a paused game holds them still.
 */
export class CritterField {
  private readonly ground: CritterGround;
  private rabbits: Ground<RabbitBrain, RabbitModel>[] = [];
  /** Crows and gulls on the ground, taking off, flying away or arriving. */
  private birds: Ground<BirdBrain, BirdModel>[] = [];
  private flocks = new Map<number, HauntFlock>();
  private beasts: Beast[] = [];
  private patches: Patch[] = [];
  /** Who is running from what, so that animals near a bolting one run from the same place. */
  private alarms: { x: number; z: number; from: Point }[] = [];
  private night = 0;
  private flyers: Flyer[] = [];
  private readonly warm: THREE.Object3D[];
  private lastTime: number | null = null;
  private spawnTimer = 0;
  private flyoverTimer: number;
  private world: 'lobby' | 'party' | null = null;
  private party: Point[] = []; private threats: Point[] = []; private fog: FogState | undefined;
  private cast: CritterCast | null = null;
  private voices = new WeakMap<object, Voice>();
  /** Its own dice, so what the wildlife says never changes what it does. */
  private speech = speechDice(0x9e3779b9);
  /** Hears the wildlife: a crow's caw, a gull's cry, a sheep's bleat, each where it happens. Absent where nothing listens (the explorer, tests). */
  onSound?: (cue: CritterCue, at: Point) => void;
  private point = new THREE.Vector3();

  constructor(private scene: THREE.Scene, private camera: THREE.OrthographicCamera, map: MapDefinition, private settings: CritterSettings, private random: Random = Math.random) {
    this.ground = new CritterGround(map);
    this.flyoverTimer = this.nextFlyover() * between(random, .3, .6);
    // One hidden specimen of each, so the scene warm-up compiles their shaders and uploads their buffers before play begins.
    const rabbit = new RabbitModel(), birds = Object.values(BIRDS).map(species => { const bird = new BirdModel(species); bird.setOverlay(true); bird.apply(restingAction()); return bird; });
    rabbit.apply({ settle: 1, hop: 0, bolt: 0, mood: 'rest', previous: 'rest', moodTime: 9, idle: 0 });
    const quads = Object.values(QUADS).map(species => { const quad = new QuadModel(species); quad.apply(restingQuad()); return quad; }), crab = new CrabModel(), butterfly = new ButterflyModel();
    crab.apply(restingCrab()); butterfly.apply(0, 0);
    this.warm = [...birds.map(bird => bird.root), rabbit.root, ...quads.map(quad => quad.root), crab.root, butterfly.root];
    for (const root of this.warm) { root.visible = false; root.position.set(0, -50, 0); scene.add(root); }
  }

  /** `time` is game seconds, so a paused game (time standing still) holds every critter still. */
  update(time: number, context: CritterContext) {
    const dt = this.lastTime === null || !(time > this.lastTime) ? 0 : Math.min(.1, time - this.lastTime);
    this.lastTime = time;
    if (!context.active) { this.hideAll(); return; }
    this.party = context.party; this.fog = context.fog; this.threats = context.threats ?? []; this.night = context.night ?? 0;
    // A lobby and a world are different places: start each already inhabited, with no one watching the first arrivals.
    const place = context.fog ? 'party' : 'lobby';
    if (place !== this.world) { this.world = place; this.clear(); this.seed(); if (place === 'lobby') this.populate(false); }
    if (dt > 0) {
      this.spawnTimer -= dt; this.flyoverTimer -= dt;
      if (this.spawnTimer <= 0) { this.spawnTimer = between(this.random, 2.5, 7); if (this.world === 'party' || !this.cast) this.spawnGround(); }
      if (this.flyoverTimer <= 0) { this.flyoverTimer = this.nextFlyover(); this.spawnFlyover(); }
    }
    this.alarms = [...this.rabbits.map(rabbit => ({ owner: rabbit.brain as { x: number; z: number }, from: rabbit.brain.fleeingFrom })), ...this.beasts.map(beast => ({ owner: beast.brain, from: beast.fear() }))]
      .flatMap(({ owner, from }) => from ? [{ x: owner.x, z: owner.z, from }] : []);
    const world = this.surroundings();
    this.updateRabbits(dt, world);
    this.updateBirds(dt, world);
    this.updateBeasts(dt, world);
    this.updatePatches(dt);
    this.updateFlyers(dt);
    this.updateHaunts(time, dt);
  }

  /** Sends a flock across the sky now; for studies and tests. */
  flyover() { this.spawnFlyover(); }

  /** Brings a rabbit, or a crow or gull landing, into the party's view now; for studies and tests. */
  introduce(kind: CritterKind) {
    if (kind === 'rabbit') this.spawnRabbit(true);
    else if (kind === 'crow' || kind === 'gull') this.spawnBird(BIRDS[kind], true, true);
    else if (kind === 'butterfly') this.spawnPatch();
    else this.spawnBeasts(kind, true);
  }

  /**
   * Sets the lobby's cast: crows and rabbits at exact spots, which stay put until something comes near. Replaces the
   * lobby's wildlife and switches off its random ground spawning. `arriving` has crows glide in and rabbits ease in,
   * rather than being there from the start. Ignored once a world is shown.
   */
  stage(cast: CritterCast, arriving = false) {
    this.cast = cast;
    if (this.world === 'lobby') { this.clearGround(); this.populate(arriving); }
  }

  private populate(arriving: boolean) {
    if (!this.cast) return;
    for (const spot of this.cast.crows) this.spawnBird(CROW, !arriving, arriving, spot, true);
    for (const spot of this.cast.rabbits) this.spawnRabbit(!arriving, spot, true);
  }

  private clearGround() {
    for (const { root } of [...this.rabbits, ...this.birds, ...this.beasts]) releaseCritter(root);
    for (const patch of this.patches) for (const { model } of patch.flutterers) releaseCritter(model.root);
    this.rabbits = []; this.birds = []; this.beasts = []; this.patches = [];
  }

  /** Retire everything. */
  clear() {
    this.clearGround();
    for (const { model } of this.flyers) releaseCritter(model.root);
    for (const flock of this.flocks.values()) flock.dispose();
    this.flyers = []; this.flocks.clear();
  }

  dispose() { this.clear(); for (const root of this.warm) releaseCritter(root); }

  /** How many of each are alive, for tests and diagnostics. */
  get counts() {
    const beasts = (kind: BeastKind) => this.beasts.filter(beast => beast.kind === kind).length;
    return { rabbits: this.rabbits.length, crows: this.of(CROW), gulls: this.of(GULL), deer: beasts('deer'), sheep: beasts('sheep'), crabs: beasts('crab'),
      butterflies: this.patches.reduce((sum, patch) => sum + patch.flutterers.length, 0), flyers: this.flyers.length, flocks: this.flocks.size };
  }

  private of(species: BirdSpecies) { return this.birds.filter(bird => bird.brain.species === species).length; }

  private nextFlyover() { return between(this.random, this.settings.flyoverSeconds[0], this.settings.flyoverSeconds[1]); }

  private hideAll() {
    for (const { root } of [...this.rabbits, ...this.birds, ...this.beasts]) root.visible = false;
    for (const patch of this.patches) for (const { model } of patch.flutterers) model.root.visible = false;
    for (const { model } of this.flyers) model.root.visible = false;
    for (const flock of this.flocks.values()) flock.hide();
  }

  private surroundings(): CritterWorld {
    return {
      free: (x, z, margin) => this.ground.free(x, z, margin),
      threat: (x, z, reach) => {
        let best: { x: number; z: number; distance: number } | null = null;
        for (const p of [...this.party, ...this.threats]) { const distance = Math.hypot(p.x - x, p.z - z); if (distance < reach && (!best || distance < best.distance)) best = { x: p.x, z: p.z, distance }; }
        // Others of the herd, or the warren, that are already running from something carry the alarm to their neighbours.
        for (const alarm of this.alarms) {
          const near = Math.hypot(alarm.x - x, alarm.z - z), distance = Math.hypot(alarm.from.x - x, alarm.from.z - z);
          if (near > .01 && near < 14 && distance < reach * 2 && (!best || distance < best.distance)) best = { x: alarm.from.x, z: alarm.from.z, distance };
        }
        return best;
      },
    };
  }

  /** Whether the party can see this spot: inside its vision, or anywhere at all when no veil is drawn. */
  private inSight(x: number, z: number) {
    const fog = this.fog;
    if (!fog || fog.revealed) return true;
    const reach = fog.visionRadius + SIGHT_PAD;
    return fog.sources.some(source => (x - source.x) ** 2 + (z - source.z) ** 2 <= reach * reach);
  }

  private onScreen(x: number, z: number, margin: number) {
    this.camera.updateMatrixWorld();
    this.point.set(x, 1, z).project(this.camera);
    return Math.abs(this.point.x) < 1 + margin && Math.abs(this.point.y) < 1 + margin;
  }

  /** Not somewhere a player would see it appear or vanish: under the veil, or without one, off-screen. */
  private unwatched(x: number, z: number) { return this.fog && !this.fog.revealed ? !this.inSight(x, z) : !this.onScreen(x, z, .25); }

  private anchors(): Point[] {
    if (this.party.length) return this.party;
    unprojectAt(this.camera, 0, 0, 0, this.point);
    return [{ x: this.point.x, z: this.point.z }];
  }

  private farFromParty(x: number, z: number, distance: number) { return this.anchors().every(anchor => Math.hypot(anchor.x - x, anchor.z - z) > distance); }

  private seed() {
    if (this.world === 'lobby' && this.cast) return;
    for (let i = 0; i < Math.ceil(this.settings.rabbits / 2); i++) this.spawnRabbit(true);
    for (let i = 0; i < Math.ceil(this.settings.crows / 2); i++) this.spawnBird(CROW, true);
    for (let i = 0; i < Math.ceil((this.settings.gulls ?? 0) / 3); i++) this.spawnGulls(true);
    for (const kind of ['deer', 'sheep', 'crab'] as const) for (let i = 0; i < Math.ceil(this.cap(kind) / (kind === 'sheep' ? 5 : 3)); i++) this.spawnBeasts(kind, true);
    for (let i = 0; i < Math.ceil(this.cap('butterfly') / 3); i++) this.spawnPatch();
  }

  /** How many of a kind may be about now: the map's number, scaled by the hour (crows, gulls and butterflies keep to the daylight; rabbits, sheep and crabs thin out at night). */
  private cap(kind: CritterKind): number {
    const s = this.settings, n = this.night, day = 1 - smooth((n - .15) / .45);
    const base = { rabbit: s.rabbits, crow: s.crows, gull: s.gulls ?? 0, deer: s.deer ?? 0, sheep: s.sheep ?? 0, crab: s.crabs ?? 0, butterfly: s.butterflies ?? 0 }[kind];
    const hours = { rabbit: 1 - .5 * n, crow: day, gull: day, deer: 1 - .3 * n, sheep: 1 - .5 * n, crab: 1 - .4 * n, butterfly: day }[kind];
    return Math.round(base * hours);
  }

  private spawnGround() {
    const have = this.counts, room = {
      rabbit: this.cap('rabbit') - have.rabbits, crow: this.cap('crow') - have.crows, gull: this.cap('gull') - have.gulls, deer: this.cap('deer') - have.deer, sheep: this.cap('sheep') - have.sheep,
      crab: this.cap('crab') - have.crabs, butterfly: this.cap('butterfly') - have.butterflies,
    };
    // Which kind comes next is chosen in proportion to the room each has (a group, such as a flock of sheep, may overshoot its cap by its own size).
    const kinds = (Object.keys(room) as (keyof typeof room)[]).filter(kind => room[kind] > 0);
    const total = kinds.reduce((sum, kind) => sum + room[kind], 0);
    if (!total) return;
    let roll = this.random() * total, chosen = kinds[0];
    for (const kind of kinds) { if (roll < room[kind]) { chosen = kind; break; } roll -= room[kind]; }
    if (chosen === 'rabbit') this.spawnRabbit(false); else if (chosen === 'crow') this.spawnBird(CROW, false); else if (chosen === 'gull') this.spawnGulls(false);
    else if (chosen === 'butterfly') this.spawnPatch(); else this.spawnBeasts(chosen, false);
  }

  /**
   * A place for something to appear: on open ground it likes and not on top of the party. Something that may be seen at once
   * (the opening population, or a bird about to glide in and land) is looked for within the party's sight, between `minimum`
   * and `maximum` metres; anything else must appear where no one is watching, so just beyond the party's sight (under the
   * veil), or, with no veil, beyond the edge of the screen.
   */
  private pickSpot(habitat: Habitat, minimum: number, maximum: number, seenAtOnce: boolean): Point | null {
    const anchors = this.anchors(), veiled = !!this.fog && !this.fog.revealed;
    const near = seenAtOnce ? minimum : veiled ? this.vision() + SIGHT_PAD + .5 : 16, far = seenAtOnce ? maximum : veiled ? this.vision() + 14 : 46;
    for (let tries = 0; tries < 24; tries++) {
      const anchor = anchors[Math.floor(this.random() * anchors.length)], angle = this.random() * Math.PI * 2, distance = between(this.random, near, far);
      let x = anchor.x + Math.cos(angle) * distance, z = anchor.z + Math.sin(angle) * distance;
      // Gulls keep to the strand, so they are looked for along the shore beside the party rather than all around it.
      if (habitat === 'gull' || habitat === 'crab') { z = anchor.z + between(this.random, -far, far); x = this.ground.coastEdge(z) + between(this.random, 3.2, habitat === 'crab' ? 8 : 12); }
      if (!this.ground.free(x, z, 1) || this.random() > this.ground.welcome(habitat, x, z)) continue;
      if (anchors.some(a => Math.hypot(a.x - x, a.z - z) < near) || anchors.every(a => Math.hypot(a.x - x, a.z - z) > far)) continue;
      if (!seenAtOnce && !this.unwatched(x, z)) continue;
      if ([...this.rabbits, ...this.birds].some(other => Math.hypot(other.brain.x - x, other.brain.z - z) < 3)) continue;
      return { x, z };
    }
    return null;
  }

  private vision() { return this.fog?.visionRadius ?? 18; }

  /** `at` places it exactly (or not at all if that is not open ground); `permanent` keeps it until something scares it. */
  private spawnRabbit(settling: boolean, at?: Point, permanent = false) {
    const spot = at && this.ground.free(at.x, at.z, .5) ? at : at ? null : this.pickSpot('rabbit', 7, this.vision() - 2, settling); if (!spot) return;
    const brain = new RabbitBrain(spot.x, spot.z, this.random() * Math.PI * 2, spot, this.random), model = new RabbitModel(Math.floor(this.random() * 3));
    this.rabbits.push(this.admit(brain, model, RABBIT_SCALE * between(this.random, .9, 1.1), settling, permanent ? Infinity : between(this.random, 120, 300)));
  }

  private spawnBird(species: BirdSpecies, settling: boolean, landing = false, at?: Point, permanent = false): Point | null {
    // Usually it flies in from out of sight and lands, which is how a bird joins a scene the party is already watching.
    const arriving = landing || (!settling && this.random() < .6);
    const spot = at && this.ground.free(at.x, at.z, .5) ? at : at ? null : this.pickSpot(species.habitat, 8, this.vision() - 3, settling || arriving); if (!spot) return null;
    const brain = new BirdBrain(species, spot.x, spot.z, this.random() * Math.PI * 2, spot, this.random), model = new BirdModel(species, this.random() * 40);
    if (arriving) {
      const angle = this.random() * Math.PI * 2, away = between(this.random, 20, 28);
      brain.arrive(spot.x + Math.cos(angle) * away, spot.z + Math.sin(angle) * away, between(this.random, 8, 11), between(this.random, 3.4, 4.6));
    }
    this.birds.push(this.admit(brain, model, species.scale * between(this.random, .92, 1.08), settling || arriving, permanent ? Infinity : between(this.random, 50, 130)));
    return spot;
  }

  /** Gulls loaf in loose groups of one to three along the strand. */
  private spawnGulls(settling: boolean) {
    const first = this.spawnBird(GULL, settling); if (!first) return;
    const extra = this.random() < .65 ? 1 + Math.floor(this.random() * 2) : 0;
    for (let i = 0; i < extra; i++) {
      const angle = this.random() * Math.PI * 2, reach = between(this.random, 1.4, 3.2);
      this.spawnBird(GULL, true, false, { x: first.x + Math.cos(angle) * reach, z: first.z + Math.sin(angle) * reach });
    }
  }

  /** A group of deer, a flock of sheep or a few crabs, together on ground their kind likes. */
  private spawnBeasts(kind: BeastKind, settling: boolean) {
    const species = kind === 'crab' ? null : QUADS[kind], [fewest, most] = species ? species.group : [2, 4];
    const spot = this.pickSpot(kind, 8, this.vision() - 3, settling); if (!spot) return;
    const count = fewest + Math.floor(this.random() * (most - fewest + 1)), spread = kind === 'sheep' ? 4 : kind === 'crab' ? 1.2 : 3;
    for (let i = 0; i < count; i++) {
      const angle = this.random() * Math.PI * 2, reach = i ? between(this.random, .6, spread) : 0, at = { x: spot.x + Math.cos(angle) * reach, z: spot.z + Math.sin(angle) * reach };
      if (!this.ground.free(at.x, at.z, kind === 'crab' ? .3 : .7)) continue;
      const heading = this.random() * Math.PI * 2, seed = this.random() * 40, tone = Math.floor(this.random() * 3);
      if (species) {
        const brain = new GrazerBrain(species, at.x, at.z, heading, spot, this.random), model = new QuadModel(species, seed, tone);
        // A fawn among the deer, now and then, is a smaller animal.
        const scale = species.scale * between(this.random, .92, 1.08) * (kind === 'deer' && i > 0 && this.random() < .4 ? .72 : 1);
        this.beasts.push(this.admitBeast(kind, brain, model.root, scale, settling, (dt, world) => { brain.update(dt, world); model.apply(brain.action); }, () => brain.heading, () => false, () => brain.fleeingFrom, () => brain.watching ? 'watch' : brain.mode));
      } else {
        const brain = new CrabBrain(at.x, at.z, heading, this.random), model = new CrabModel(seed, tone);
        this.beasts.push(this.admitBeast('crab', brain, model.root, CRAB_SCALE * between(this.random, .85, 1.2), settling, (dt, world) => { brain.update(dt, world); model.apply(brain.action); }, () => brain.heading, () => brain.gone, () => null));
      }
    }
  }

  private admitBeast(kind: BeastKind, brain: { x: number; z: number }, root: THREE.Object3D, scale: number, shown: boolean,
    step: Beast['step'], heading: () => number, gone: () => boolean, fear: () => Point | null, mode: () => string = () => ''): Beast {
    root.visible = false; this.scene.add(root);
    return { kind, brain, root, scale, appear: shown ? 1 : 0, age: 0, lifetime: between(this.random, 150, 400), step, heading, gone, fear, mode };
  }

  /** Two to four butterflies working a patch of flowers within the party's sight; they fade in. */
  private spawnPatch() {
    const spot = this.pickSpot('butterfly', 5, this.vision() - 2, true); if (!spot) return;
    const flutterers: Flutterer[] = [], count = 2 + Math.floor(this.random() * 3), tone = Math.floor(this.random() * 4);
    for (let i = 0; i < count; i++) {
      const flutterer = new Flutterer(spot, this.random, this.random() < .7 ? tone : Math.floor(this.random() * 4));
      flutterer.model.root.visible = false; this.scene.add(flutterer.model.root); flutterer.update(0); flutterers.push(flutterer);
    }
    this.patches.push({ centre: spot, flutterers, scales: flutterers.map(flutterer => flutterer.model.root.scale.x), appear: 0, age: 0, lifetime: between(this.random, 60, 140) });
  }

  private updateBeasts(dt: number, world: CritterWorld) {
    this.beasts = this.beasts.filter(beast => {
      beast.age += dt;
      if (beast.gone() || this.farFromParty(beast.brain.x, beast.brain.z, CULL_DISTANCE) || (beast.age > beast.lifetime && this.unwatched(beast.brain.x, beast.brain.z))) { releaseCritter(beast.root); return false; }
      if (dt > 0) beast.step(dt, world); else beast.step(0, world);
      if (dt > 0 && beast.kind === 'sheep') this.sheepVoice(beast, dt);
      else if (dt > 0 && beast.kind === 'deer') this.deerVoice(beast);
      this.place(beast, dt, beast.brain.x, 0, beast.brain.z, beast.heading());
      return true;
    });
  }

  private updatePatches(dt: number) {
    this.patches = this.patches.filter(patch => {
      patch.age += dt;
      // They go with the daylight, and when their flowers are left behind.
      const leave = this.night > .5 || patch.age > patch.lifetime || this.farFromParty(patch.centre.x, patch.centre.z, CULL_DISTANCE);
      patch.appear += ((leave || !this.inSight(patch.centre.x, patch.centre.z) ? 0 : 1) - patch.appear) * (1 - Math.exp(-dt * 1.2));
      if (leave && patch.appear < .02) { for (const { model } of patch.flutterers) releaseCritter(model.root); return false; }
      patch.flutterers.forEach((flutterer, index) => {
        if (dt > 0) flutterer.update(dt);
        const root = flutterer.model.root;
        root.visible = patch.appear > .02; root.scale.setScalar(patch.scales[index] * smooth(patch.appear));
      });
      return true;
    });
  }

  private admit<B extends { x: number; z: number }, M extends { root: THREE.Object3D }>(brain: B, model: M, scale: number, shown: boolean, lifetime: number): Ground<B, M> {
    model.root.visible = false; this.scene.add(model.root);
    return { brain, model, root: model.root, scale, appear: shown ? 1 : 0, age: 0, lifetime };
  }

  private updateRabbits(dt: number, world: CritterWorld) {
    this.rabbits = this.rabbits.filter(rabbit => {
      const { brain } = rabbit;
      rabbit.age += dt; rabbit.brain.drowsy = this.night;
      // Old rabbits slip away, but only while no one is looking.
      if (this.farFromParty(brain.x, brain.z, CULL_DISTANCE) || (rabbit.age > rabbit.lifetime && this.unwatched(brain.x, brain.z))) { releaseCritter(rabbit.root); return false; }
      if (dt > 0) { brain.update(dt, world); this.rabbitVoice(rabbit); }
      rabbit.model.apply(brain.action);
      this.place(rabbit, dt, brain.x, 0, brain.z, brain.heading);
      return true;
    });
  }

  private updateBirds(dt: number, world: CritterWorld) {
    this.birds = this.birds.filter(crow => {
      const { brain } = crow, aloft = brain.mode === 'fly' || brain.mode === 'launch', grounded = brain.mode === 'idle' || brain.mode === 'walk';
      crow.age += dt;
      // Crows and gulls go to roost at nightfall: a bird the party can see takes wing, one it cannot just goes.
      if (this.night > .6 && grounded && crow.age < crow.lifetime) crow.lifetime = crow.age - 1;
      const gone = this.farFromParty(brain.x, brain.z, aloft ? FLIGHT_CULL_DISTANCE : CULL_DISTANCE) || (brain.mode === 'fly' && brain.flightTime > MAX_FLIGHT_SECONDS)
        || (grounded && crow.age > crow.lifetime && this.unwatched(brain.x, brain.z));
      if (gone) { releaseCritter(crow.root); return false; }
      // At the end of its stay a bird the party can see takes wing rather than vanishing.
      if (grounded && crow.age > crow.lifetime) brain.depart(this.random() * Math.PI * 2);
      if (dt > 0) { brain.update(dt, world); this.birdVoice(crow, dt); }
      crow.model.setOverlay(brain.airborne);
      crow.model.apply(brain.action);
      this.place(crow, dt, brain.x, brain.y, brain.z, brain.heading);
      return true;
    });
  }

  /** Positions a figure, easing it in and out as it crosses the edge of the party's sight. Birds high above are always shown. */
  private place(critter: { root: THREE.Object3D; scale: number; appear: number }, dt: number, x: number, y: number, z: number, heading: number) {
    const target = y > 2 || this.inSight(x, z) ? 1 : 0;
    critter.appear += (target - critter.appear) * (1 - Math.exp(-dt * 5));
    if (Math.abs(target - critter.appear) < .004) critter.appear = target;
    critter.root.visible = critter.appear > .01;
    critter.root.scale.setScalar(critter.scale * smooth(critter.appear));
    critter.root.position.set(x, y + this.ground.height(x, z), z); critter.root.rotation.y = heading;
  }

  private voiceOf(owner: object, first: [number, number]) {
    let voice = this.voices.get(owner);
    if (!voice) this.voices.set(owner, voice = { mode: null, next: between(this.speech, ...first), crop: between(this.speech, 2, 12) });
    return voice;
  }

  private call(species: BirdSpecies, at: Point) {
    this.onSound?.(species === GULL ? (this.speech() < .4 ? 'gull call' : 'gull cry') : (this.speech() < .28 ? 'crow double call' : 'crow call'), at);
  }

  /** A bird speaks as it takes off and now and then while it stands, and its wings are heard as it springs; only where the party can see it. */
  private birdVoice(bird: Ground<BirdBrain, BirdModel>, dt: number) {
    const { brain } = bird, voice = this.voiceOf(bird, [4, 30]), was = voice.mode;
    voice.mode = brain.mode;
    if (!this.onSound || !(brain.airborne || this.inSight(brain.x, brain.z))) return;
    const at = { x: brain.x, z: brain.z }, grounded = brain.mode === 'idle' || brain.mode === 'walk';
    if (brain.mode === 'launch' && was !== 'launch') { if (this.speech() < .8) this.call(brain.species, at); }
    else if (brain.mode === 'fly' && was === 'launch') this.onSound(brain.species === GULL ? 'gull wings' : 'crow wings', at);
    else if (was === 'land' && grounded && this.speech() < .35) this.call(brain.species, at);
    else if (grounded && (voice.next -= dt) <= 0) { voice.next = between(this.speech, 14, 40); this.call(brain.species, at); }
  }

  /** A bird crossing high above calls every so often as it goes. */
  private flyerVoice(flyer: Flyer, dt: number) {
    const voice = this.voiceOf(flyer, [2, 8]);
    if (!this.onSound || (voice.next -= dt) > 0) return;
    voice.next = between(this.speech, 6, 16);
    this.call(flyer.species, { x: flyer.bird.position.x, z: flyer.bird.position.z });
  }

  /** A sheep bleats now and then and, while it grazes, is heard cropping the grass; only where the party can see it. */
  private sheepVoice(sheep: Beast, dt: number) {
    const voice = this.voiceOf(sheep, [10, 60]), at = { x: sheep.brain.x, z: sheep.brain.z };
    if (!this.onSound || !this.inSight(at.x, at.z)) return;
    if ((voice.next -= dt) <= 0) { voice.next = between(this.speech, 30, 90); this.onSound('sheep bleat', at); }
    if (sheep.mode() === 'graze' && (voice.crop -= dt) <= 0) { voice.crop = between(this.speech, 8, 22); this.onSound('sheep crop', at); }
  }

  /** A rabbit scurries away as it bolts; only where the party can see it. */
  private rabbitVoice(rabbit: Ground<RabbitBrain, RabbitModel>) {
    const { brain } = rabbit, voice = this.voiceOf(rabbit, [0, 0]), was = voice.mode;
    voice.mode = brain.fleeingFrom ? 'bolt' : null;
    if (voice.mode === 'bolt' && was !== 'bolt' && this.onSound && this.inSight(brain.x, brain.z)) this.onSound('rabbit scurry', { x: brain.x, z: brain.z });
  }

  /** A deer snorts as it freezes to watch something close and its hooves are heard as it breaks into its gallop; only where the party can see it. */
  private deerVoice(deer: Beast) {
    const voice = this.voiceOf(deer, [0, 0]), was = voice.mode, mode = deer.mode();
    voice.mode = mode === 'flee' || mode === 'watch' ? mode : null;
    if (voice.mode === was || !voice.mode || !this.onSound || !this.inSight(deer.brain.x, deer.brain.z)) return;
    this.onSound(voice.mode === 'flee' ? 'deer gallop' : 'deer snort', { x: deer.brain.x, z: deer.brain.z });
  }

  private spawnFlyover() {
    if (this.flyers.length >= MAX_FLYERS) return;
    const roll = this.random(), count = roll < .5 ? 1 : roll < .8 ? 2 : roll < .95 ? 3 : 4;
    // Over the shore a flock is usually gulls; elsewhere it is crows.
    const anchor = this.anchors()[0], species = (this.settings.gulls ?? 0) > 0 && this.ground.shoreDistance(anchor.x, anchor.z) < COAST_REACH && this.random() < .6 ? GULL : CROW;
    const plan = planFlyover(this.camera, this.random, between(this.random, 7.5, 12));
    // Faster the further the view is zoomed out, so a crossing takes about as long on screen whatever the zoom.
    const speed = between(this.random, 7.5, 10) * THREE.MathUtils.clamp(1 / Math.sqrt(this.camera.zoom), .75, 2.4);
    for (let i = 0; i < count; i++) {
      const model = new BirdModel(species, this.random() * 40); model.setOverlay(true);
      model.root.scale.setScalar(species.scale * between(this.random, .95, 1.1)); model.root.visible = false; this.scene.add(model.root);
      // The first bird leads; the others trail it, weaving to either side and a little higher or lower.
      const bird = new FlyoverBird(species, plan, speed * between(this.random, .94, 1.06), i * between(this.random, 1.5, 6), i ? between(this.random, -3.2, 3.2) : 0, i ? between(this.random, -1.2, 1.2) : 0, this.random);
      this.flyers.push({ bird, model, species });
    }
  }

  private updateFlyers(dt: number) {
    this.flyers = this.flyers.filter(flyer => {
      const { bird, model } = flyer;
      if (dt > 0) { bird.update(dt); this.flyerVoice(flyer, dt); }
      if (bird.done) { releaseCritter(model.root); return false; }
      model.apply(bird.action);
      model.root.position.copy(bird.position); model.root.rotation.y = bird.heading; model.root.visible = true;
      return true;
    });
  }

  /** Brings each haunt's flock out while the party is near its airspace and puts it away when the party has left. */
  private updateHaunts(time: number, dt: number) {
    const anchors = this.anchors();
    for (const [index, haunt] of (this.settings.haunts ?? []).entries()) {
      const nearest = Math.min(...anchors.map(anchor => hauntDistance(haunt, this.ground, anchor)));
      let flock = this.flocks.get(index);
      if (flock && nearest > HAUNT_LEAVE) { flock.dispose(); this.flocks.delete(index); continue; }
      if (!flock && nearest <= HAUNT_RANGE) { flock = new HauntFlock(haunt, this.ground, this.scene, this.random); this.flocks.set(index, flock); }
      flock?.update(time, dt);
    }
  }
}
